import {
  LATEST_ACTIVITY_TIME_KEY,
  TRACKED_ACTIVITY_EVENTS,
  TRACKED_KEYBOARD_EVENTS,
  USAGE_REPORTING_GRACE_PERIOD_MS,
} from './UserAnalyticsContants';

let scrollTimeout: NodeJS.Timeout | null = null;

function trackActivity() {
  const currentTime = Date.now();
  localStorage.setItem(LATEST_ACTIVITY_TIME_KEY, currentTime.toString());
}

function handleScroll() {
  // Throttle scroll events to once per second to avoid performance issues
  if (scrollTimeout) {
    return;
  }

  scrollTimeout = setTimeout(() => {
    trackActivity();
    scrollTimeout = null;
  }, 1000);
}

export function trackUserActivity(event?: Event) {
  // Handle keyboard navigation
  if (event?.type === 'keydown') {
    const keyEvent = event as KeyboardEvent;
    const isNavigationKey = TRACKED_KEYBOARD_EVENTS.includes(keyEvent.key);

    if (!isNavigationKey) {
      return;
    }
  }

  // Skip direct scroll events (they'll be handled by handleScroll)
  if (event?.type === 'scroll') {
    return;
  }

  trackActivity();
}

export function initializeActivityTracking() {
  if (!localStorage.getItem(LATEST_ACTIVITY_TIME_KEY)) {
    trackActivity();
  }

  TRACKED_ACTIVITY_EVENTS.forEach((event) => {
    if (event === 'scroll') {
      // Use passive scroll listener with throttled handler
      document.addEventListener(event, handleScroll, {
        passive: true,
      });
    } else {
      document.addEventListener(event, trackUserActivity, {
        passive: true,
        capture: true,
      });
    }
  });
}

/**
 * Cleans up activity tracking event listeners and scroll timeout.
 */
export function cleanupActivityTracking() {
  TRACKED_ACTIVITY_EVENTS.forEach((event) => {
    if (event === 'scroll') {
      document.removeEventListener(event, handleScroll);
    } else {
      document.removeEventListener(event, trackUserActivity, { capture: true });
    }
  });

  // Clear any pending scroll timeout
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
    scrollTimeout = null;
  }
}

export function getLatestActivityTime(): Date | null {
  const storedTime = localStorage.getItem(LATEST_ACTIVITY_TIME_KEY);

  if (!storedTime) {
    return null;
  }

  const date = new Date(parseInt(storedTime, 10));

  // If the date is invalid, return null
  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
}

export function isLatestActivityWithinGracePeriod(now: Date): boolean {
  const lastActivity = getLatestActivityTime();
  if (!lastActivity) {
    return false;
  }

  return (
    now.getTime() - lastActivity.getTime() <= USAGE_REPORTING_GRACE_PERIOD_MS
  );
}
